import { useEffect, useRef } from "react";

// Tracks whether a customer is currently interacting with the kiosk, so the
// remote-update controller can defer a soft reload until the kiosk is idle
// (between customers) instead of yanking the screen mid-checkout.
//
// "Busy" is a composition of independent holders (a Symbol per component
// instance) rather than a single boolean, so multiple sources compose safely
// and a component that unmounts while busy is automatically released. The
// kiosk is idle when no holder is busy.

const busyHolders = new Set<symbol>();
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

function setBusyHolder(key: symbol, busy: boolean) {
  const had = busyHolders.has(key);
  if (busy && !had) {
    busyHolders.add(key);
    emit();
  } else if (!busy && had) {
    busyHolders.delete(key);
    emit();
  }
}

/** Subscribe to idle/busy transitions. Returns the unsubscribe function. */
export function subscribeActivity(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** True when no holder is currently busy (safe to reload). */
export function getIsIdle(): boolean {
  return busyHolders.size === 0;
}

/**
 * Register the calling component as a busy holder while `isBusy` is true.
 * Derive `isBusy` from the component's existing state (modal open / request in
 * flight) and pass it in — this keeps the signal in sync with renders and
 * releases the holder automatically on unmount.
 */
export function useKioskBusy(isBusy: boolean): void {
  const keyRef = useRef<symbol | null>(null);
  if (keyRef.current === null) {
    keyRef.current = Symbol("kiosk-busy");
  }

  useEffect(() => {
    setBusyHolder(keyRef.current as symbol, isBusy);
  }, [isBusy]);

  useEffect(() => {
    const key = keyRef.current as symbol;
    return () => setBusyHolder(key, false);
  }, []);
}
